import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import { format, formatDistanceToNowStrict } from 'date-fns';
import { decode } from 'he';
import { Trash2, Plus, Columns2 } from 'lucide-react';
import DOMPurify from 'dompurify';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';
import { RichTextEditor } from '../ui/rich-text-editor';
import { ArticleBodyEditor } from '../content/ArticleBodyEditor';
import { VersionCompareView } from '../content/VersionCompare';
import { resolveDisplayMediaUrl } from '../../utils/mediaUrl';
import { TopicMediaPanel } from '../content/TopicMediaPanel';
import { MediaUrlField } from '../content/MediaUrlField';
import {
  GET_CONTENT_ITEM,
  GET_ACCOUNT_TAXONOMY,
  GET_ACCOUNT_TOPIC_FIELDS,
  GET_TOPIC_VERSIONS,
  SAVE_CONTENT_ITEM,
  RESTORE_TOPIC_VERSION,
  PUBLISH_TOPIC,
  SCHEDULE_TOPIC,
  UNPUBLISH_TOPIC,
  DELETE_TINYCMS_ARTICLE,
  ATOMIZE_TOPIC,
  SUMMARIZE_CONTENT_DRAFT,
  GET_TINYCMS_ARTICLE,
  UPDATE_TINYCMS_ARTICLE,
  AUTOSAVE_TINYCMS_ARTICLE,
  GET_COWORK_MCP_CONNECTOR,
} from '../../graphql';
import {
  GetContentItemResponse,
  GetAccountTaxonomyResponse,
  GetAccountTopicFieldsResponse,
  GetTopicVersionsResponse,
  ProvenanceTaxonomies,
  VerifiedFact,
  NamedEntity,
  TopicVersion,
  SaveContentItemInput,
  AccountTopicField,
} from '../../types/graphql';
import { useAccount } from '../../context/AccountContext';
import { ArticlePreview } from '../cms/CmsPages';
import { ContentUnavailable } from '../content/ContentUnavailable';
import { Loader } from '../common/PageLoader';
import { primaryCategory } from '../cms/categories';
import { useAddons } from '../cms/addons';
import { useTinycmsSites } from '../cms/sites';
import { showInkToast } from '../cms/InkToast';
import {
  ArticleApprovals,
  ArticleChecks,
  CmsPublishCard,
  DatasetCard,
  ReachCard,
  SearchSharing,
  TagEditor,
  cmsStatus,
  isHttpUrl,
  isValidSlug,
  type CmsArticle,
  type SeoDraft,
} from '../content/ArticleWorkflow';

type AutosaveStatus = 'idle' | 'dirty' | 'saving' | 'saved' | 'error';

const AUTOSAVE_MS = 2000;

const inputClass =
  'w-full px-3 py-2 bg-form-dark text-cmp-ink rounded border border-cmp-line-2 focus:outline-none focus:ring-2 focus:ring-primary';

// Source-type vocabulary the atomizer emits (see ATOMIZATION-METHODOLOGY §7).
// Values are the canonical slugs; labels mirror the product wireframe.
const SOURCE_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: 'stated_in_article', label: 'Stated in article' },
  { value: 'attributed_fact', label: 'Attributed fact' },
  { value: 'disputed_claim', label: 'Disputed claim' },
  { value: 'direct_quote', label: 'Direct quote' },
  { value: 'data_cited', label: 'Data / statistic cited' },
  { value: 'official_record', label: 'Official record referenced' },
];

const sourceTypeLabel = (value?: string): string =>
  SOURCE_TYPE_OPTIONS.find((o) => o.value === value)?.label || value || '';

function FieldLabel({ children }: { children: ReactNode }) {
  return (
    <label className="block font-cmp-mono text-[0.66rem] tracking-[0.14em] uppercase text-cmp-ink-3 mb-1.5">
      {children}
    </label>
  );
}

type EntityDescKey = 'role' | 'context';

function EntityListSection({
  title,
  entities,
  descKey,
  descPlaceholder,
  isEditing,
  onChange,
}: {
  title: string;
  entities: NamedEntity[];
  descKey: EntityDescKey;
  descPlaceholder: string;
  isEditing: boolean;
  onChange: (next: NamedEntity[]) => void;
}) {
  const patch = (i: number, p: Partial<NamedEntity>) =>
    onChange(entities.map((e, j) => (j === i ? { ...e, ...p } : e)));

  return (
    <div className="mb-6 last:mb-0">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-cmp-ink text-sm font-semibold">{title}</h4>
        {isEditing && (
          <Button
            variant="outline"
            onClick={() => onChange([...entities, { name: '', [descKey]: '' } as NamedEntity])}
            className="gap-1 h-7 text-xs"
          >
            <Plus className="w-3 h-3" /> Add
          </Button>
        )}
      </div>
      {entities.length === 0 ? (
        <p className="text-xs text-cmp-ink-3">None.</p>
      ) : !isEditing ? (
        <div className="space-y-2">
          {entities.map((e, i) => (
            <div key={i} className="border border-cmp-line rounded p-3">
              <p className="text-sm text-cmp-ink">{e.name}</p>
              {(e[descKey] as string | undefined) && (
                <p className="text-xs text-cmp-ink-2 mt-0.5">{e[descKey]}</p>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-2">
          {entities.map((e, i) => (
            <div key={i} className="border border-cmp-line rounded p-3 flex items-start gap-2">
              <div className="flex-1 grid grid-cols-2 gap-2">
                <div>
                  <FieldLabel>Name</FieldLabel>
                  <Input
                    value={e.name}
                    onChange={(ev) => patch(i, { name: ev.target.value })}
                    className={inputClass}
                    placeholder="Full name"
                  />
                </div>
                <div>
                  <FieldLabel>{descKey === 'role' ? 'Role' : 'Context'}</FieldLabel>
                  <Input
                    value={(e[descKey] as string) || ''}
                    onChange={(ev) => patch(i, { [descKey]: ev.target.value })}
                    className={inputClass}
                    placeholder={descPlaceholder}
                  />
                </div>
              </div>
              <button
                onClick={() => onChange(entities.filter((_, j) => j !== i))}
                className="text-error hover:opacity-80 mt-6"
                title="Delete"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

interface EditableFields {
  title: string;
  summary: string;
  bodyText: string;
  mediaUrl: string;
  destinationUrl: string;
  category: string;
  keywords: string;
  contentType: string;
}

const emptyFields: EditableFields = {
  title: '',
  summary: '',
  bodyText: '',
  mediaUrl: '',
  destinationUrl: '',
  category: '',
  keywords: '',
  contentType: 'webpage',
};

/**
 * The tinycms sidecar half of the working copy (slug, SEO, hero alt, author,
 * tags). It saves through updateTinycmsArticle when editing finishes rather
 * than on every autosave, since each of those writes records a version.
 */
interface CmsFields extends SeoDraft {
  heroAlt: string;
  author: string;
  tags: string[];
}

const emptyCmsFields: CmsFields = {
  slug: '',
  metaTitle: '',
  metaDescription: '',
  canonicalUrl: '',
  ogImageUrl: '',
  heroAlt: '',
  author: '',
  tags: [],
};

function cmsFieldsFrom(article?: CmsArticle | null): CmsFields {
  const c = article?.content;
  if (!c) return emptyCmsFields;
  return {
    slug: c.slug || article?.slug || '',
    metaTitle: c.seo?.metaTitle || '',
    metaDescription: c.seo?.metaDescription || '',
    canonicalUrl: c.seo?.canonicalUrl || '',
    ogImageUrl: c.seo?.ogImageUrl || '',
    heroAlt: c.hero?.alt || '',
    author: c.author || '',
    tags: c.tags || [],
  };
}

/** Only what changed, in updateTinycmsArticle's shape. */
function cmsPatch(before: CmsFields, after: CmsFields) {
  const patch: Record<string, unknown> = {};
  if (after.slug !== before.slug) patch.slug = after.slug;
  if (after.heroAlt !== before.heroAlt) patch.hero = { alt: after.heroAlt.trim() || null };
  if (after.author !== before.author) patch.author = after.author.trim() || null;
  if (after.tags.join('\u0000') !== before.tags.join('\u0000')) patch.tags = after.tags;
  const seo: Record<string, unknown> = {};
  for (const k of ['metaTitle', 'metaDescription'] as const) {
    if (after[k] !== before[k]) seo[k] = after[k].trim() || null;
  }
  for (const k of ['canonicalUrl', 'ogImageUrl'] as const) {
    if (after[k] !== before[k]) seo[k] = after[k] || null;
  }
  if (Object.keys(seo).length) patch.seo = seo;
  return patch;
}

/** Why the sidecar fields can't save yet, or '' when they can. */
function cmsFieldsProblem(f: CmsFields): string {
  if (!isValidSlug(f.slug)) {
    return 'The URL slug must be lowercase letters and digits separated by single hyphens.';
  }
  if (f.canonicalUrl && !isHttpUrl(f.canonicalUrl))
    return 'The canonical URL must start with https://.';
  if (f.ogImageUrl && !isHttpUrl(f.ogImageUrl))
    return 'The share image URL must start with https://.';
  return '';
}

function publishStatusLabel(status?: string | null) {
  if (status === 'published') return 'Published';
  if (status === 'scheduled') return 'Scheduled';
  return 'Draft';
}

function topicLabel(value?: string | null) {
  const raw = primaryCategory(value);
  if (/^[a-z0-9]{1,8}$/i.test(raw)) return raw.toUpperCase();
  return raw;
}

function dimensionLabel(key: string) {
  const words = key.replace(/[_-]+/g, ' ').trim();
  if (!words) return key;
  return words.charAt(0).toUpperCase() + words.slice(1);
}

function statusColor(status?: string | null) {
  if (status === 'published') return 'var(--ink-green)';
  if (status === 'scheduled') return 'var(--ink-accent)';
  return 'var(--ink-ink3)';
}

const QUARTER_MINUTES = [0, 15, 30, 45] as const;

/** Format a Date for datetime-local (local wall clock, minutes only). */
function toDatetimeLocalValue(date: Date): string {
  return format(date, "yyyy-MM-dd'T'HH:mm");
}

/** Next quarter-hour mark strictly after `from` (:00 / :15 / :30 / :45). */
function nextQuarterHour(from: Date = new Date()): Date {
  const d = new Date(from);
  d.setSeconds(0, 0);
  d.setMilliseconds(0);
  // Move to the next 15-minute boundary after `from`.
  const totalMins = d.getHours() * 60 + d.getMinutes();
  const nextTotal = Math.floor(totalMins / 15) * 15 + 15;
  d.setHours(0, 0, 0, 0);
  d.setMinutes(nextTotal);
  return d;
}

/** Snap a datetime-local value to the nearest quarter hour (ties round up). */
function snapDatetimeLocalToQuarter(value: string): string {
  if (!value) return value;
  const d = new Date(value);
  if (isNaN(d.getTime())) return value;
  d.setSeconds(0, 0);
  d.setMilliseconds(0);
  const totalMins = d.getHours() * 60 + d.getMinutes();
  const snapped = Math.round(totalMins / 15) * 15;
  d.setHours(0, 0, 0, 0);
  d.setMinutes(snapped);
  return toDatetimeLocalValue(d);
}

function isQuarterHour(date: Date): boolean {
  return (
    date.getSeconds() === 0 &&
    date.getMilliseconds() === 0 &&
    (QUARTER_MINUTES as readonly number[]).includes(date.getMinutes())
  );
}

const STREAM_PLAYER = /^https:\/\/customer-[a-z0-9]+\.cloudflarestream\.com\/[a-f0-9]{32}\/iframe/i;

// Scraped/edited HTML is untrusted and can be double entity-encoded, so decode
// until stable, then sanitize before rendering or persisting.
function toSafeHtml(raw?: string | null): string {
  if (!raw) return '';
  let decoded = raw;
  for (let i = 0; i < 3; i++) {
    const next = decode(decoded);
    if (next === decoded) break;
    decoded = next;
  }
  const clean = DOMPurify.sanitize(decoded, {
    USE_PROFILES: { html: true },
    ADD_TAGS: ['iframe'],
    ADD_ATTR: ['allow', 'allowfullscreen', 'loading'],
  });
  if (!/<iframe/i.test(clean)) return clean;
  // The only frame kept is the site's own video player (as on the public site).
  const t = document.createElement('template');
  t.innerHTML = clean;
  t.content.querySelectorAll('iframe').forEach((f) => {
    if (!STREAM_PLAYER.test(f.getAttribute('src') || '')) f.remove();
  });
  return t.innerHTML;
}

/**
 * Count entries in topics.media_items, which is polymorphic by ingest path:
 * RSS writes { images: [], videos: [] }, while WordPress and the media layer
 * write a flat array of { role, url, … }. Reading one shape blindly reports 0
 * for content ingested through the other.
 */
function countMediaItems(mediaItems: unknown, kind: 'images' | 'videos'): number {
  if (!mediaItems) return 0;
  if (Array.isArray(mediaItems)) {
    // Flat form carries no video entries today; everything linked is an image.
    return kind === 'images' ? mediaItems.length : 0;
  }
  const bag = mediaItems as { images?: unknown[]; videos?: unknown[] };
  return (kind === 'images' ? bag.images?.length : bag.videos?.length) ?? 0;
}

export function ContentDetail() {
  const navigate = useNavigate();
  const { id: topicId } = useParams<{ id: string }>();

  const { accountId, accounts, selectAccountById } = useAccount();
  // Atomize (facts, entities, summarize) is an add-on. Without it the article
  // still opens and edits; its actions are hidden, and so are the facts and
  // entities it produces unless the site holds it (switched off still shows them).
  const { atomizeOn, atomizeHeld, reachOn } = useAddons();
  const { sites } = useTinycmsSites();
  const siteUrl = sites.find((s) => s.accountId === accountId)?.siteUrl || '';

  const {
    data,
    loading,
    error: itemError,
    refetch,
  } = useQuery<GetContentItemResponse>(GET_CONTENT_ITEM, {
    variables: { id: topicId },
    skip: !topicId,
  });
  const { data: taxData } = useQuery<GetAccountTaxonomyResponse>(GET_ACCOUNT_TAXONOMY, {
    variables: { accountId },
    skip: !accountId,
  });
  const { data: topicFieldsData } = useQuery<GetAccountTopicFieldsResponse>(
    GET_ACCOUNT_TOPIC_FIELDS,
    { variables: { accountId }, skip: !accountId }
  );
  const {
    data: versionsData,
    loading: versionsLoading,
    refetch: refetchVersions,
  } = useQuery<GetTopicVersionsResponse>(GET_TOPIC_VERSIONS, {
    variables: { topicId },
    skip: !topicId,
  });

  // The tinycms view of the same topic: working copy vs live, its hash, checks,
  // SEO. Null for a topic not brought under tinycms (the server answers
  // NOT_FOUND), which keeps the older publish controls below.
  const {
    data: cmsData,
    loading: cmsLoading,
    refetch: refetchCms,
  } = useQuery<{ tinycmsArticle: CmsArticle | null }>(GET_TINYCMS_ARTICLE, {
    variables: { accountId, id: topicId },
    skip: !accountId || !topicId,
    errorPolicy: 'all',
    fetchPolicy: 'cache-and-network',
  });
  const cms = cmsData?.tinycmsArticle || null;
  const [updateCms, { loading: updatingCms }] = useMutation(UPDATE_TINYCMS_ARTICLE);
  const [autosaveCms] = useMutation(AUTOSAVE_TINYCMS_ARTICLE);
  const [approvalsKey, setApprovalsKey] = useState(0);

  const item = data?.topic;

  // A link can open another site's article (any site you belong to). Its
  // tinycms state, fields and media all live on that site, so follow it there
  // once; the site menu's own switch goes to Today, so this never fights it.
  const ownerId = item?.accountId || null;
  const ownerSite = ownerId ? accounts.find((a) => a.id === ownerId) : undefined;
  const otherSite = Boolean(ownerSite && ownerId !== accountId);
  const followedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!otherSite || !ownerSite || followedRef.current === item?.id) return;
    followedRef.current = item?.id ?? null;
    selectAccountById(ownerSite.id);
    showInkToast(`Switched to ${ownerSite.name}, where this article lives.`);
  }, [otherSite, ownerSite, item?.id, selectAccountById]);
  const dimensions = useMemo(() => taxData?.accountTaxonomy || [], [taxData]);
  const topicFieldDefs = useMemo(
    () => (topicFieldsData?.accountTopicFields || []).filter((f) => f.isActive !== false),
    [topicFieldsData]
  );
  const versions = versionsData?.topicVersions || [];
  // The version waiting for approval is the agent's when nobody saved it in the
  // app (an MCP save records no person). Then the panel asks for approval and a
  // status line above the article says who is asking (Prototype v7).
  const latestVersion = versions.length
    ? versions.reduce((a, b) => (b.versionNumber > a.versionNumber ? b : a))
    : null;
  const agentMade = Boolean(
    cms &&
      cms.state.hasUnpublishedChanges &&
      !cms.state.scheduled &&
      latestVersion &&
      !latestVersion.createdBy
  );
  const { data: connectorData } = useQuery<{
    coworkMcpConnector: { label?: string | null } | null;
  }>(GET_COWORK_MCP_CONNECTOR, { variables: { accountId }, skip: !accountId || !agentMade });
  const agentName = connectorData?.coworkMcpConnector?.label?.trim() || 'Your agent';

  const [isEditing, setIsEditing] = useState(false);
  // The full-screen preview of the working copy (UX review v5).
  const [previewOpen, setPreviewOpen] = useState(false);
  const [selected, setSelected] = useState<Record<string, string[]>>({});
  const [customFieldValues, setCustomFieldValues] = useState<Record<string, string>>({});
  const [facts, setFacts] = useState<VerifiedFact[]>([]);
  const [keyPeople, setKeyPeople] = useState<NamedEntity[]>([]);
  const [keyOrganizations, setKeyOrganizations] = useState<NamedEntity[]>([]);
  const [keyLocations, setKeyLocations] = useState<NamedEntity[]>([]);
  const [fields, setFields] = useState<EditableFields>(emptyFields);
  const [cmsFields, setCmsFields] = useState<CmsFields>(emptyCmsFields);
  // What the sidecar held when editing started (or was last saved): the patch base.
  const cmsBaseRef = useRef<CmsFields>(emptyCmsFields);
  // The title/summary/body last sent through the tinycms write (JSON).
  const cmsTextRef = useRef('');
  const [heroPicking, setHeroPicking] = useState(false);
  const [message, setMessage] = useState<string>('');
  const [expandedVersionId, setExpandedVersionId] = useState<string | null>(null);
  const [compareSelecting, setCompareSelecting] = useState(false);
  const [compareSelectedIds, setCompareSelectedIds] = useState<string[]>([]);
  const [compareActive, setCompareActive] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [scheduleAt, setScheduleAt] = useState('');
  const [autosaveStatus, setAutosaveStatus] = useState<AutosaveStatus>('idle');
  const [autosavedAt, setAutosavedAt] = useState<Date | null>(null);
  const skipAutosaveRef = useRef(true);
  const autosaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const autosaveInFlightRef = useRef(false);

  const seedFromItem = () => {
    const tax: ProvenanceTaxonomies = item?.taxonomies || {};
    const next: Record<string, string[]> = {};
    for (const [key, terms] of Object.entries(tax)) next[key] = terms.map((t) => t.term);
    skipAutosaveRef.current = true;
    setSelected(next);
    const cf = item?.customFields || {};
    const cfDraft: Record<string, string> = {};
    for (const def of topicFieldDefs) {
      const raw = cf[def.fieldKey];
      if (raw === undefined || raw === null) {
        cfDraft[def.fieldKey] = def.fieldType === 'boolean' ? 'false' : '';
      } else if (def.fieldType === 'json' || typeof raw === 'object') {
        cfDraft[def.fieldKey] = JSON.stringify(raw, null, 2);
      } else if (typeof raw === 'boolean') {
        cfDraft[def.fieldKey] = raw ? 'true' : 'false';
      } else {
        cfDraft[def.fieldKey] = String(raw);
      }
    }
    setCustomFieldValues(cfDraft);
    setFacts((item?.verifiedFacts || []).map((f) => ({ ...f })));
    setKeyPeople((item?.keyPeople || []).map((e) => ({ ...e })));
    setKeyOrganizations((item?.keyOrganizations || []).map((e) => ({ ...e })));
    setKeyLocations((item?.keyLocations || []).map((e) => ({ ...e })));
    setFields({
      title: item?.title || '',
      summary: item?.summary || '',
      bodyText: item?.bodyText || '',
      // Seed from the display pointer — that is what the field now writes.
      // Falls back to media_url for RSS items, which carry a real URL there.
      mediaUrl: item?.primaryMediaUrl || item?.mediaUrl || '',
      destinationUrl: item?.destinationUrl || '',
      category: item?.category || '',
      keywords: item?.keywords || '',
      contentType: item?.contentType || 'webpage',
    });
    const sidecar = cmsFieldsFrom(cms);
    cmsBaseRef.current = sidecar;
    cmsTextRef.current = JSON.stringify({
      title: (item?.title || '').trim(),
      summary: item?.summary || null,
      body: item?.bodyText || null,
    });
    setCmsFields(sidecar);
    setAutosaveStatus('idle');
  };

  const setCmsField = (patch: Partial<CmsFields>) =>
    setCmsFields((prev) => ({ ...prev, ...patch }));
  const cmsDirty = Boolean(cms) && Object.keys(cmsPatch(cmsBaseRef.current, cmsFields)).length > 0;

  /** The tinycms text fields as they would be sent (empty → null). */
  const cmsText = () => {
    const blank = (v: string) => (!v.trim() || v === '<p></p>' ? null : v);
    return {
      title: fields.title.trim(),
      summary: blank(fields.summary),
      body: blank(fields.bodyText),
    };
  };

  /**
   * Save a tinycms article's title, summary and body (what changed since the
   * last send) through updateTinycmsArticle. An autosave records no version;
   * `final` (Done or Save) also sends the sidecar fields (slug, SEO, alt,
   * author, tags), and `forceVersion` (Save) records a version even when
   * nothing else changed.
   */
  const persistCmsArticle = async ({
    final,
    forceVersion = false,
  }: {
    final: boolean;
    forceVersion?: boolean;
  }) => {
    if (!cms || !accountId || !topicId) return;
    const text = cmsText();
    const textKey = JSON.stringify(text);
    if (!text.title) {
      // The server keeps the old title rather than store an empty one.
      setMessage('Failed: An article needs a title.');
      throw new Error('An article needs a title.');
    }
    const input: Record<string, unknown> = textKey !== cmsTextRef.current ? { ...text } : {};
    const sidecar = final ? cmsPatch(cmsBaseRef.current, cmsFields) : {};
    if (Object.keys(sidecar).length) {
      const problem = cmsFieldsProblem(cmsFields);
      if (problem) {
        setMessage(`Failed: ${problem}`);
        throw new Error(problem);
      }
      Object.assign(input, sidecar);
    }
    const versioned = forceVersion || Object.keys(sidecar).length > 0;
    if (!Object.keys(input).length && !versioned) return;
    try {
      await (versioned ? updateCms : autosaveCms)({
        variables: { accountId, id: topicId, input },
      });
    } catch (err) {
      setMessage(`Failed: ${err instanceof Error ? err.message : 'Could not save'}`);
      throw err;
    }
    cmsTextRef.current = textKey;
    if (final) cmsBaseRef.current = cmsFields;
  };

  const refreshAll = () =>
    Promise.all([refetch(), refetchVersions(), refetchCms()]).then(() =>
      setApprovalsKey((k) => k + 1)
    );

  const toggleNoindex = async () => {
    if (!cms || !accountId || !topicId) return;
    try {
      await updateCms({
        variables: {
          accountId,
          id: topicId,
          input: { seo: { noindex: !cms.content.seo?.noindex } },
        },
      });
      await refetchVersions();
    } catch (err) {
      setMessage(`Failed: ${err instanceof Error ? err.message : 'Could not save'}`);
    }
  };

  const setField = (key: keyof EditableFields, value: string) =>
    setFields((prev) => ({ ...prev, [key]: value }));

  const [saveContentItem, { loading: saving }] = useMutation(SAVE_CONTENT_ITEM, {
    onError: (e) => setMessage(`Failed: ${e.message}`),
  });
  const [restoreVersion, { loading: restoring }] = useMutation(RESTORE_TOPIC_VERSION, {
    onError: (e) => setMessage(`Failed: ${e.message}`),
  });
  const [publishTopic, { loading: publishing }] = useMutation(PUBLISH_TOPIC, {
    onError: (e) => setMessage(`Failed: ${e.message}`),
  });
  const [scheduleTopic, { loading: scheduling }] = useMutation(SCHEDULE_TOPIC, {
    onError: (e) => setMessage(`Failed: ${e.message}`),
  });
  const [unpublishTopic, { loading: unpublishing }] = useMutation(UNPUBLISH_TOPIC, {
    onError: (e) => setMessage(`Failed: ${e.message}`),
  });
  const [atomizeTopic, { loading: atomizing }] = useMutation(ATOMIZE_TOPIC, {
    onError: (e) => setMessage(`Failed: ${e.message}`),
  });
  // Evicting the deleted item keeps every cached list (My Content, topic pickers)
  // in step without refetching them.
  const [deleteArticle, { loading: deleting }] = useMutation(DELETE_TINYCMS_ARTICLE, {
    onError: (e) => setMessage(e.message),
    update: (cache, { data }) => {
      const deletedId = data?.deleteTinycmsArticle?.articleId;
      if (!deletedId) return;
      cache.evict({ id: cache.identify({ __typename: 'Topic', id: deletedId }) });
      cache.gc();
    },
  });
  const [summarizeDraft] = useMutation(SUMMARIZE_CONTENT_DRAFT);
  const publishBusy = publishing || scheduling || unpublishing || deleting;

  // Toolbar "Summarize to bullets": atomize the draft body on the fly and return
  // key-takeaway bullets for the editor to insert.
  const requestSummary = async (plainText: string): Promise<string[]> => {
    const { data } = await summarizeDraft({
      variables: { accountId, title: fields.title, bodyText: plainText },
    });
    return data?.summarizeContentDraft?.bullets ?? [];
  };

  // Seed editable state from the loaded item (not while actively editing —
  // autosave must not reset the cursor via refetch).
  useEffect(() => {
    if (isEditing) return;
    seedFromItem();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item, cms, isEditing, topicFieldDefs]);

  // Sanitized body HTML for webpage/article content, falling back to the
  // summary/excerpt when no full body was captured.
  const bodyHtml = useMemo(() => toSafeHtml(item?.bodyText || item?.summary), [item]);
  const summaryHtml = useMemo(() => toSafeHtml(item?.summary), [item]);

  // Which dimensions/values were synced (read-only display).
  const syncedByKey = useMemo(() => {
    const out: Record<string, string[]> = {};
    const tax: ProvenanceTaxonomies = item?.taxonomies || {};
    for (const [key, terms] of Object.entries(tax)) {
      const synced = terms.filter((t) => t.provenance === 'synced').map((t) => t.term);
      if (synced.length) out[key] = synced;
    }
    return out;
  }, [item]);

  const toggleTerm = (key: string, term: string) =>
    setSelected((prev) => {
      const cur = prev[key] || [];
      return {
        ...prev,
        [key]: cur.includes(term) ? cur.filter((t) => t !== term) : [...cur, term],
      };
    });

  const setFreeform = (key: string, value: string) =>
    setSelected((prev) => ({
      ...prev,
      [key]: value
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean),
    }));

  const buildSaveInput = (): SaveContentItemInput => {
    const taxPayload = Object.entries(selected)
      .map(([key, terms]) => ({ key, terms }))
      .filter((d) => d.terms.length > 0);
    const summaryHtmlValue = fields.summary === '<p></p>' ? '' : toSafeHtml(fields.summary);
    const bodyHtmlValue = fields.bodyText === '<p></p>' ? '' : toSafeHtml(fields.bodyText);
    const customFields: Record<string, unknown> = {};
    for (const def of topicFieldDefs) {
      const raw = customFieldValues[def.fieldKey];
      if (raw === undefined) continue;
      if (def.fieldType === 'json') {
        const trimmed = (raw || '').trim();
        if (!trimmed) {
          customFields[def.fieldKey] = null;
        } else {
          try {
            customFields[def.fieldKey] = JSON.parse(trimmed);
          } catch {
            throw new Error(`Custom field "${def.fieldKey}" must be valid JSON`);
          }
        }
      } else if (def.fieldType === 'boolean') {
        customFields[def.fieldKey] = raw === 'true';
      } else if (def.fieldType === 'number') {
        customFields[def.fieldKey] = raw === '' ? null : Number(raw);
      } else {
        customFields[def.fieldKey] = raw === '' ? null : raw;
      }
    }
    return {
      // A tinycms article's title, summary and body save through the tinycms
      // write (persistCmsArticle), which turns video placeholders into players
      // and checks them; this older write would store them raw.
      ...(cms
        ? {}
        : {
            title: fields.title.trim(),
            summary: summaryHtmlValue,
            bodyText: bodyHtmlValue,
            contentType: fields.contentType || undefined,
          }),
      mediaUrl: fields.mediaUrl.trim(),
      destinationUrl: fields.destinationUrl.trim(),
      category: fields.category.trim(),
      keywords: fields.keywords.trim(),
      taxonomies: taxPayload,
      customFields: topicFieldDefs.length ? customFields : undefined,
      facts: facts.map((f) => ({
        fact: f.fact,
        source_quote: f.source_quote || '',
        attribution: f.attribution || '',
        source_type: f.source_type || '',
        date: f.date || '',
      })),
      keyPeople: keyPeople
        .filter((e) => e.name.trim())
        .map((e) => ({ name: e.name.trim(), role: e.role || '' })),
      keyOrganizations: keyOrganizations
        .filter((e) => e.name.trim())
        .map((e) => ({ name: e.name.trim(), role: e.role || '' })),
      keyLocations: keyLocations
        .filter((e) => e.name.trim())
        .map((e) => ({ name: e.name.trim(), context: e.context || '' })),
    };
  };

  const persistEdit = async (createVersion: boolean) => {
    if (!topicId) return;
    let input: SaveContentItemInput;
    try {
      input = buildSaveInput();
    } catch (err) {
      setMessage(`Failed: ${err instanceof Error ? err.message : 'Invalid custom fields'}`);
      throw err;
    }
    await saveContentItem({
      variables: {
        id: topicId,
        input,
        // A tinycms article's version is recorded by its own write, once.
        createVersion: cms ? false : createVersion,
      },
    });
    if (cms) await persistCmsArticle({ final: createVersion, forceVersion: createVersion });
  };

  const startEdit = () => {
    seedFromItem();
    setMessage('');
    setIsEditing(true);
    // Allow the next user keystroke to mark dirty / autosave.
    setTimeout(() => {
      skipAutosaveRef.current = false;
    }, 0);
  };

  const cancelEdit = async () => {
    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current);
      autosaveTimerRef.current = null;
    }
    // Flush pending edits quietly so Done doesn't drop the last few seconds.
    if (autosaveStatus === 'dirty' || autosaveStatus === 'error') {
      try {
        autosaveInFlightRef.current = true;
        setAutosaveStatus('saving');
        await persistEdit(false);
        setAutosavedAt(new Date());
      } catch {
        // Keep editing if flush fails.
        autosaveInFlightRef.current = false;
        setAutosaveStatus('error');
        return;
      }
      autosaveInFlightRef.current = false;
    }
    try {
      await persistCmsArticle({ final: true });
    } catch {
      return; // persistCmsArticle set the message; keep editing
    }
    setMessage('');
    setIsEditing(false);
    setHeroPicking(false);
    setAutosaveStatus('idle');
    await Promise.all([refetch(), refetchCms(), refetchVersions()]);
  };

  const saveAll = async () => {
    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current);
      autosaveTimerRef.current = null;
    }
    try {
      await persistEdit(true);
      setAutosaveStatus('saved');
      setAutosavedAt(new Date());
      await Promise.all([refetch(), refetchVersions(), refetchCms()]);
      setMessage(
        cms?.state.live
          ? 'Saved to the working copy. Readers still see the live version.'
          : 'Saved to the working copy.'
      );
      setIsEditing(false);
      setHeroPicking(false);
    } catch {
      // onError handlers and persistCmsArticle set the message
    }
  };

  // Debounced server autosave while editing (no version history entry).
  useEffect(() => {
    if (!isEditing || !topicId) return;
    if (skipAutosaveRef.current) {
      skipAutosaveRef.current = false;
      return;
    }
    setAutosaveStatus('dirty');
    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    autosaveTimerRef.current = setTimeout(async () => {
      if (autosaveInFlightRef.current) return;
      autosaveInFlightRef.current = true;
      setAutosaveStatus('saving');
      try {
        await persistEdit(false);
        setAutosavedAt(new Date());
        setAutosaveStatus('saved');
      } catch {
        setAutosaveStatus('error');
      } finally {
        autosaveInFlightRef.current = false;
      }
    }, AUTOSAVE_MS);
    return () => {
      if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing, topicId, fields, selected, facts, keyPeople, keyOrganizations, keyLocations]);

  useEffect(() => {
    if (!isEditing) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (autosaveStatus === 'dirty' || autosaveStatus === 'saving' || cmsDirty) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [isEditing, autosaveStatus, cmsDirty]);

  const autosaveLabel = (() => {
    if (!isEditing) return null;
    if (autosaveStatus === 'saving') return 'Saving…';
    if (autosaveStatus === 'dirty') return 'Unsaved changes…';
    if (autosaveStatus === 'error') return 'Autosave failed — will retry';
    if (autosaveStatus === 'saved' && autosavedAt) {
      return `Saved ${format(autosavedAt, 'p')}`;
    }
    return 'Autosave on';
  })();

  // E opens the editor (the review's shortcut), unless typing somewhere.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() !== 'e' || e.metaKey || e.ctrlKey || e.altKey) return;
      const el = e.target as HTMLElement | null;
      if (el?.closest('input, textarea, select, [contenteditable="true"]')) return;
      if (isEditing || previewOpen || !item) return;
      e.preventDefault();
      startEdit();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing, previewOpen, item, cms]);

  const handlePublish = async () => {
    if (!topicId) return;
    try {
      await publishTopic({ variables: { id: topicId } });
      await Promise.all([refetch(), refetchVersions()]);
      setMessage('Published.');
      setScheduleOpen(false);
    } catch {
      // onError handlers set the message
    }
  };

  const handleAtomize = async () => {
    if (!topicId) return;
    if (isEditing) {
      setMessage('Failed: Finish or save your edits before atomizing.');
      return;
    }
    const hasBody = Boolean((item?.bodyText || item?.summary || '').trim());
    if (!hasBody) {
      setMessage('Failed: Add body (or summary) content before atomizing.');
      return;
    }
    if (
      item?.atomizedAt &&
      !window.confirm(
        'This item was atomized before. Re-run atomization? Existing facts, entities, and derived classification will be replaced.'
      )
    ) {
      return;
    }
    try {
      setMessage('Atomizing… this may take a minute.');
      await atomizeTopic({ variables: { topicId } });
      await Promise.all([refetch(), refetchVersions()]);
      setMessage('Atomization complete — facts, entities, and classification updated.');
    } catch {
      // onError handlers set the message
    }
  };

  const handleSchedule = async () => {
    if (!topicId || !scheduleAt) {
      setMessage('Failed: Choose a future date and time.');
      return;
    }
    const snapped = snapDatetimeLocalToQuarter(scheduleAt);
    if (snapped !== scheduleAt) setScheduleAt(snapped);
    const when = new Date(snapped);
    if (isNaN(when.getTime()) || when.getTime() <= Date.now()) {
      setMessage('Failed: Scheduled publish time must be in the future.');
      return;
    }
    if (!isQuarterHour(when)) {
      setMessage('Failed: Publish time must be on :00, :15, :30, or :45.');
      return;
    }
    try {
      await scheduleTopic({
        variables: { id: topicId, publishedDate: when.toISOString() },
      });
      await Promise.all([refetch(), refetchVersions()]);
      setMessage('Scheduled for publish.');
      setScheduleOpen(false);
    } catch {
      // onError handlers set the message
    }
  };

  const handleUnpublish = async () => {
    if (!topicId) return;
    if (!window.confirm('Move this item back to draft? The publish date will be cleared.')) {
      return;
    }
    try {
      await unpublishTopic({ variables: { id: topicId } });
      await Promise.all([refetch(), refetchVersions()]);
      setMessage('Moved to draft.');
      setScheduleOpen(false);
    } catch {
      // onError handlers set the message
    }
  };

  const handleDelete = async () => {
    if (!topicId) return;
    const live = item?.publishStatus && item.publishStatus !== 'draft';
    if (
      !window.confirm(
        live
          ? 'Delete this article? It comes off your site now. You can restore it from Recently deleted for 30 days.'
          : 'Delete this draft? You can restore it from Recently deleted for 30 days.'
      )
    ) {
      return;
    }
    try {
      await deleteArticle({ variables: { accountId, id: topicId } });
      navigate('/content?view=articles');
    } catch {
      // onError sets the message (e.g. a page still links to it)
    }
  };

  const handleRestore = async (version: TopicVersion) => {
    if (
      !window.confirm(
        `Restore version ${version.versionNumber}? This replaces the current content and creates a new revision.`
      )
    ) {
      return;
    }
    try {
      await restoreVersion({
        variables: { topicId, versionId: version.id },
      });
      await Promise.all([refetch(), refetchVersions(), refetchCms()]);
      setMessage(`Restored version ${version.versionNumber}.`);
      setIsEditing(false);
      setExpandedVersionId(null);
    } catch {
      // onError handlers set the message
    }
  };

  const changeTypeLabel = (changeType: string) => {
    if (changeType === 'created') return 'Created';
    if (changeType === 'restored') return 'Restored';
    return 'Updated';
  };

  const toggleCompareSelect = (versionId: string) => {
    setCompareSelectedIds((prev) => {
      if (prev.includes(versionId)) return prev.filter((id) => id !== versionId);
      if (prev.length >= 3) return prev;
      return [...prev, versionId];
    });
  };

  const startCompareSelect = () => {
    setCompareSelecting(true);
    setCompareActive(false);
    setCompareSelectedIds([]);
    setExpandedVersionId(null);
  };

  const cancelCompareSelect = () => {
    setCompareSelecting(false);
    setCompareActive(false);
    setCompareSelectedIds([]);
  };

  const runCompare = () => {
    if (compareSelectedIds.length < 2) return;
    setCompareActive(true);
  };

  const compareVersions = versions.filter((v) => compareSelectedIds.includes(v.id));

  const renderPrimary = () => {
    if (!item) return null;
    const type = (isEditing ? fields.contentType : item.contentType) || 'webpage';
    // tinycms articles are type "article"; older topics edit as "webpage".
    if (isEditing && (type === 'webpage' || (cms && type === 'article'))) {
      return (
        <ArticleBodyEditor
          value={fields.bodyText}
          onChange={(html) => setField('bodyText', html)}
          accountId={accountId}
          allowVideo={Boolean(cms)}
          storedForm={cms ? undefined : toSafeHtml}
          readOnlyHtml={bodyHtml}
          onRequestSummary={accountId && atomizeOn ? requestSummary : undefined}
        />
      );
    }
    if (type === 'video') {
      const src = resolveDisplayMediaUrl(
        (isEditing ? fields.mediaUrl : item.primaryMediaUrl || item.mediaUrl) || ''
      );
      return src ? (
        <video controls className="w-full max-h-[28rem] rounded bg-black" src={src} />
      ) : (
        <p className="text-cmp-ink-2 text-sm">No video URL.</p>
      );
    }
    if (type === 'image') {
      const src = resolveDisplayMediaUrl(
        (isEditing ? fields.mediaUrl : item.primaryMediaUrl || item.mediaUrl) || ''
      );
      return src ? (
        <img
          src={src}
          alt={item.title || ''}
          className="w-full max-h-[28rem] object-contain rounded"
        />
      ) : (
        <p className="text-cmp-ink-2 text-sm">No image URL.</p>
      );
    }
    if (bodyHtml) {
      return (
        <div
          className="ink-detail-body [&_a]:text-cmp-bronze-2 [&_a]:underline [&_a]:underline-offset-2 [&_a:hover]:text-cmp-ink [&_ul]:mb-3 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:mb-3 [&_ol]:list-decimal [&_ol]:pl-5 [&_h1]:text-xl [&_h1]:font-semibold [&_h1]:mb-2 [&_h2]:text-lg [&_h2]:font-semibold [&_h2]:mb-2 [&_h3]:text-base [&_h3]:font-semibold [&_h3]:mb-2 [&_img]:my-3 [&_img]:max-w-full [&_img]:h-auto [&_img]:rounded"
          dangerouslySetInnerHTML={{ __html: bodyHtml }}
        />
      );
    }
    return <p className="text-cmp-ink-2 text-sm">No body content extracted.</p>;
  };

  const heroUrl = resolveDisplayMediaUrl(
    (isEditing ? fields.mediaUrl : item?.primaryMediaUrl || item?.mediaUrl) || ''
  );

  return (
    <div className="ink-split">
      {loading || otherSite || (cmsLoading && !cmsData) ? (
        // Held until the tinycms view is in, so the older controls never flash.
        <main className="ink-split-main">
          <Loader label="Loading" className="ink-set-empty" />
        </main>
      ) : !item ? (
        // Deleted, another site's, or an unknown id: the server answers empty
        // for all three, so say so instead of loading forever.
        <main className="ink-split-main">
          <ContentUnavailable id={topicId} error={itemError?.message} />
        </main>
      ) : (
        <>
          {previewOpen ? (
            <ArticlePreview
              articleId={item.id}
              title={decode(item.title || 'Untitled')}
              onClose={() => setPreviewOpen(false)}
            />
          ) : null}
          <main className="ink-split-main">
            <div className="ink-detail-bar">
              <button
                type="button"
                className="ink-detail-back"
                onClick={() => navigate('/content')}
              >
                ← Content{item.category ? ` / ${topicLabel(item.category)}` : ''}
              </button>
              <div className="ink-detail-actions">
                {isEditing ? (
                  <>
                    <span className="ink-detail-edit-note">
                      {cms?.state.live
                        ? 'Editing the working copy · live stays unchanged until you publish'
                        : 'Editing the working copy'}
                      {autosaveLabel ? ` · ${autosaveLabel}` : ''}
                    </span>
                    <button
                      type="button"
                      className="ink-btn"
                      onClick={() => void cancelEdit()}
                      disabled={
                        saving ||
                        updatingCms ||
                        restoring ||
                        publishBusy ||
                        autosaveStatus === 'saving'
                      }
                    >
                      Done
                    </button>
                    <button
                      type="button"
                      className="ink-btn ink-btn-primary"
                      onClick={() => void saveAll()}
                      disabled={
                        saving ||
                        updatingCms ||
                        restoring ||
                        publishBusy ||
                        autosaveStatus === 'saving'
                      }
                    >
                      {saving || updatingCms ? 'Saving…' : 'Save to working copy'}
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      className="ink-btn ink-btn-quiet"
                      onClick={() => setPreviewOpen(true)}
                    >
                      Preview
                    </button>
                    <button
                      type="button"
                      className="ink-btn ink-btn-primary"
                      onClick={startEdit}
                      disabled={restoring || publishBusy || atomizing}
                      title="Edit (E)"
                    >
                      Edit
                      <span className="ink-kbd" aria-hidden="true">
                        E
                      </span>
                    </button>
                  </>
                )}
              </div>
            </div>

            {agentMade && !isEditing && latestVersion ? (
              <div className="ink-agent-ask" role="status">
                <span className="ink-agent-ask-mark" aria-hidden="true">
                  {/* Initials of a named agent ("MC"); a plain mark otherwise. */}
                  {agentName === 'Your agent'
                    ? '✦'
                    : agentName
                        .split(/\s+/)
                        .map((w) => w[0])
                        .join('')
                        .slice(0, 2)
                        .toUpperCase()}
                </span>
                <p>
                  <strong>{agentName}</strong> wants to publish this
                  {(() => {
                    const at = new Date(
                      /^\d{10,}$/.test(String(latestVersion.createdAt))
                        ? Number(latestVersion.createdAt)
                        : latestVersion.createdAt
                    );
                    return Number.isNaN(at.getTime())
                      ? ''
                      : ` · ${formatDistanceToNowStrict(at, { addSuffix: true })}`;
                  })()}
                </p>
                <span className="ink-muted">Waiting on your approval · decide in the panel →</span>
              </div>
            ) : null}
            <div className="ink-detail-hero">
              {heroUrl ? (
                ((isEditing ? fields.contentType : item.contentType) || 'webpage') === 'video' ? (
                  <video controls src={heroUrl} />
                ) : (
                  <img
                    src={heroUrl}
                    alt={(isEditing ? cmsFields.heroAlt : cms?.content.hero?.alt) || ''}
                  />
                )
              ) : (
                <span className="ink-detail-hero-label">hero image</span>
              )}
              {cms && heroUrl ? (
                (() => {
                  const alt = (isEditing ? cmsFields.heroAlt : cms.content.hero?.alt) || '';
                  return (
                    <span className="ink-detail-hero-alt" data-ok={alt ? 'true' : undefined}>
                      {alt ? `Alt: ${alt}` : 'No alt text'}
                    </span>
                  );
                })()
              ) : !heroUrl && !isEditing ? (
                <span className="ink-detail-hero-alt" data-ok="true">
                  No hero image
                </span>
              ) : null}
              {isEditing ? (
                <span className="ink-detail-hero-tools">
                  <button type="button" onClick={() => setHeroPicking((v) => !v)}>
                    {heroUrl ? 'Replace' : 'Add image'}
                  </button>
                  {fields.mediaUrl ? (
                    <button
                      type="button"
                      onClick={() => {
                        setField('mediaUrl', '');
                        setCmsField({ heroAlt: '' });
                      }}
                    >
                      Remove
                    </button>
                  ) : null}
                </span>
              ) : null}
            </div>
            {isEditing && heroPicking ? (
              <div style={{ marginTop: 12, maxWidth: 760 }}>
                <MediaUrlField
                  accountId={accountId}
                  value={fields.mediaUrl}
                  onChange={(url) => setField('mediaUrl', url)}
                  inputClassName={inputClass}
                  hasAttachedImages={countMediaItems(item.mediaItems, 'images') > 0}
                />
              </div>
            ) : null}
            {isEditing && cms && fields.mediaUrl ? (
              <label className="ink-edit-frame" style={{ marginTop: 12 }}>
                <div className="ink-edit-frame-label">
                  ALT TEXT · read aloud by screen readers · required
                </div>
                <input
                  className="ink-input ink-edit-field"
                  value={cmsFields.heroAlt}
                  onChange={(e) => setCmsField({ heroAlt: e.target.value })}
                  placeholder="Describe the image"
                  data-bad={!cmsFields.heroAlt.trim() ? 'true' : undefined}
                />
              </label>
            ) : null}

            {isEditing ? (
              <label className="ink-edit-frame" style={{ marginTop: 24 }}>
                <div className="ink-edit-frame-label">
                  TITLE · headline, page title and link text
                </div>
                <textarea
                  className="ink-input ink-edit-field ink-edit-title"
                  rows={2}
                  value={fields.title}
                  // A title is one line: Enter and pasted line breaks become spaces.
                  onChange={(e) => setField('title', e.target.value.replace(/\s*\n+\s*/g, ' '))}
                  placeholder="Title"
                />
              </label>
            ) : (
              <h1
                className="ink-detail-title ink-detail-editable"
                onDoubleClick={startEdit}
                title="Double-click to edit"
              >
                {decode(item.title || 'Content')}
              </h1>
            )}

            <div className="ink-detail-byline">
              {(() => {
                const st = cms
                  ? cmsStatus(cms.state)
                  : {
                      label: publishStatusLabel(item.publishStatus),
                      color: statusColor(item.publishStatus),
                    };
                return (
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      fontWeight: 500,
                      color: st.color,
                    }}
                  >
                    <span className="ink-dot" style={{ background: st.color }} />
                    {st.label}
                  </span>
                );
              })()}
              {cms?.content.author || item.dataSource ? (
                <>
                  <span>·</span>
                  <span>
                    By{' '}
                    <b style={{ fontWeight: 500, color: 'var(--ink-accent)' }}>
                      {cms?.content.author || item.dataSource?.name}
                    </b>
                  </span>
                </>
              ) : null}
              {item.publishedDate ? (
                <>
                  <span>·</span>
                  <span>
                    {item.publishStatus === 'scheduled' ? 'Goes live ' : ''}
                    {format(new Date(item.publishedDate), 'MMM d, yyyy')}
                  </span>
                </>
              ) : null}
            </div>

            {isEditing ? (
              <div className="ink-edit-frame" style={{ marginTop: 28 }}>
                <div className="ink-edit-frame-label">
                  SUMMARY · used in cards, feeds and LLM context
                </div>
                <RichTextEditor
                  value={fields.summary}
                  onChange={(html) => setField('summary', html)}
                  placeholder="Summary"
                />
              </div>
            ) : summaryHtml && item.bodyText ? (
              <div className="ink-detail-summary">
                <div dangerouslySetInnerHTML={{ __html: summaryHtml }} />
                <div className="ink-detail-summary-note">
                  SUMMARY · used in cards, feeds and LLM context
                </div>
              </div>
            ) : null}

            {message ? (
              <p
                className="ink-rail-note"
                style={{
                  color: message.startsWith('Failed') ? 'var(--ink-amber)' : 'var(--ink-green)',
                }}
              >
                {message}
              </p>
            ) : null}

            <section
              data-part="body"
              className={isEditing ? undefined : 'ink-detail-editable'}
              onDoubleClick={isEditing ? undefined : startEdit}
            >
              <div className="mb-6">{renderPrimary()}</div>
            </section>
            {cms ? (
              <>
                <SearchSharing
                  article={cms}
                  editing={isEditing}
                  draft={cmsFields}
                  onDraft={setCmsField}
                  title={decode((isEditing ? fields.title : item.title) || '')}
                  summary={(isEditing ? fields.summary : item.summary) || ''}
                  heroUrl={heroUrl}
                  siteBase={`${(siteUrl || '').replace(/^https?:\/\//, '').replace(/\/$/, '')}/article/`}
                  onToggleNoindex={() => void toggleNoindex()}
                  togglingNoindex={updatingCms}
                />
                <DatasetCard article={cms} />
              </>
            ) : null}
            <section data-part="images" className="ink-detail-block">
              <h3>Images</h3>
              {accountId && topicId ? (
                <TopicMediaPanel
                  accountId={accountId}
                  topicId={topicId}
                  // A card leads with its front; an article leads with
                  // a featured image.
                  defaultRole={item.contentType === 'card' ? 'front' : 'featured'}
                  onChanged={() => void refetch()}
                />
              ) : null}
            </section>
            <section data-part="fields" className="ink-detail-block">
              <h3>Custom fields</h3>
              <p className="text-xs text-cmp-ink-3 mb-4">
                Extra structured data on each article. Configure them in Content settings.
              </p>
              {topicFieldDefs.length === 0 ? (
                <p className="text-xs text-cmp-ink-2">No custom fields defined for this account.</p>
              ) : (
                <div className="space-y-4 max-w-2xl">
                  {topicFieldDefs.map((def: AccountTopicField) => {
                    const value = customFieldValues[def.fieldKey] ?? '';
                    const enumChoices = Array.isArray(def.options) ? def.options.map(String) : [];
                    return (
                      <div key={def.fieldKey}>
                        <p className="text-xs text-cmp-ink mb-1">
                          {def.label}
                          {def.required && (
                            <span className="ml-2 text-[10px] uppercase text-warning">
                              required
                            </span>
                          )}
                          <span className="ml-2 font-mono text-[10px] text-cmp-ink-3">
                            {def.fieldKey}
                          </span>
                        </p>
                        {def.description && (
                          <p className="text-[11px] text-cmp-ink-3 mb-1">{def.description}</p>
                        )}
                        {!isEditing ? (
                          <pre className="text-xs text-cmp-ink whitespace-pre-wrap break-words bg-form-dark rounded border border-cmp-line p-2">
                            {value || '—'}
                          </pre>
                        ) : def.fieldType === 'boolean' ? (
                          <select
                            className={inputClass}
                            value={value || 'false'}
                            onChange={(e) =>
                              setCustomFieldValues((prev) => ({
                                ...prev,
                                [def.fieldKey]: e.target.value,
                              }))
                            }
                          >
                            <option value="true">true</option>
                            <option value="false">false</option>
                          </select>
                        ) : def.fieldType === 'enum' ? (
                          <select
                            className={inputClass}
                            value={value}
                            onChange={(e) =>
                              setCustomFieldValues((prev) => ({
                                ...prev,
                                [def.fieldKey]: e.target.value,
                              }))
                            }
                          >
                            <option value="">—</option>
                            {enumChoices.map((c) => (
                              <option key={c} value={c}>
                                {c}
                              </option>
                            ))}
                          </select>
                        ) : def.fieldType === 'text' || def.fieldType === 'json' ? (
                          <textarea
                            className={`${inputClass} min-h-[120px] font-mono text-xs`}
                            value={value}
                            onChange={(e) =>
                              setCustomFieldValues((prev) => ({
                                ...prev,
                                [def.fieldKey]: e.target.value,
                              }))
                            }
                            placeholder={def.fieldType === 'json' ? '{ ... }' : def.label}
                          />
                        ) : (
                          <Input
                            className={inputClass}
                            type={def.fieldType === 'number' ? 'number' : 'text'}
                            value={value}
                            onChange={(e) =>
                              setCustomFieldValues((prev) => ({
                                ...prev,
                                [def.fieldKey]: e.target.value,
                              }))
                            }
                            placeholder={def.label}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
            {atomizeHeld && (
              <>
                <section data-part="entities" className="ink-detail-block">
                  <h3>Entities</h3>
                  <p className="text-xs text-cmp-ink-3 mb-4">
                    People, organizations, and locations extracted from the article.
                  </p>
                  <EntityListSection
                    title="Key People"
                    entities={keyPeople}
                    descKey="role"
                    descPlaceholder="Role or relevance to the story"
                    isEditing={isEditing}
                    onChange={setKeyPeople}
                  />
                  <EntityListSection
                    title="Key Organizations"
                    entities={keyOrganizations}
                    descKey="role"
                    descPlaceholder="Role or relevance to the story"
                    isEditing={isEditing}
                    onChange={setKeyOrganizations}
                  />
                  <EntityListSection
                    title="Key Locations"
                    entities={keyLocations}
                    descKey="context"
                    descPlaceholder="Why this location is relevant"
                    isEditing={isEditing}
                    onChange={setKeyLocations}
                  />
                </section>
                <section data-part="facts" className="ink-detail-block">
                  <h3>Verified facts</h3>
                  {isEditing && (
                    <div className="flex justify-end mb-3">
                      <Button
                        variant="outline"
                        onClick={() =>
                          setFacts((prev) => [
                            ...prev,
                            {
                              fact: '',
                              attribution: '',
                              source_type: '',
                              date: '',
                              source_quote: '',
                            },
                          ])
                        }
                        className="gap-1"
                      >
                        <Plus className="w-3 h-3" /> Fact
                      </Button>
                    </div>
                  )}
                  <p className="text-xs text-cmp-ink-3 mb-3">
                    Only facts explicitly stated in the article. These are the verified atoms
                    available for reassembly — do not infer, extrapolate, or supplement with
                    external knowledge.
                  </p>
                  {facts.length === 0 && <p className="text-xs text-cmp-ink-2">No facts.</p>}
                  {!isEditing ? (
                    <div className="space-y-3">
                      {facts.map((f, i) => (
                        <div key={i} className="border border-cmp-line rounded p-3 space-y-2">
                          <div className="flex items-center justify-between gap-2 flex-wrap">
                            <span className="text-xs text-cmp-ink-2">
                              #{i + 1}
                              {f.date ? ` · ${f.date}` : ''}
                            </span>
                            {f.source_type && (
                              <Badge variant="outline" className="text-cmp-ink">
                                {sourceTypeLabel(f.source_type)}
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-cmp-ink">{f.fact}</p>
                          {f.attribution && (
                            <p className="text-xs text-cmp-ink-2">
                              <span className="text-cmp-ink-3">Attribution: </span>
                              {f.attribution}
                            </p>
                          )}
                          {f.source_quote && (
                            <div className="text-xs border-l-2 border-cmp-line-2 pl-2">
                              <span className="block text-cmp-ink-3 mb-0.5">
                                Original article text
                              </span>
                              <span className="italic text-cmp-ink">{f.source_quote}</span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {facts.map((f, i) => {
                        const patch = (p: Partial<VerifiedFact>) =>
                          setFacts((prev) => prev.map((x, j) => (j === i ? { ...x, ...p } : x)));
                        return (
                          <div key={i} className="border border-cmp-line rounded p-3 space-y-3">
                            <div className="flex items-center justify-between">
                              <span className="text-xs font-medium text-cmp-ink">#{i + 1}</span>
                              <button
                                onClick={() => setFacts((prev) => prev.filter((_, j) => j !== i))}
                                className="text-error hover:opacity-80"
                                title="Delete fact"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <FieldLabel>Date / Time Reference</FieldLabel>
                                <Input
                                  value={f.date || ''}
                                  onChange={(e) => patch({ date: e.target.value })}
                                  className={inputClass}
                                  placeholder="e.g. July 3, 2026"
                                />
                              </div>
                              <div>
                                <FieldLabel>Source Type</FieldLabel>
                                <select
                                  value={f.source_type || ''}
                                  onChange={(e) => patch({ source_type: e.target.value })}
                                  className={inputClass}
                                >
                                  <option value="">— Select —</option>
                                  {SOURCE_TYPE_OPTIONS.map((o) => (
                                    <option key={o.value} value={o.value}>
                                      {o.label}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            </div>
                            <div>
                              <FieldLabel>Attribution</FieldLabel>
                              <Input
                                value={f.attribution || ''}
                                onChange={(e) => patch({ attribution: e.target.value })}
                                className={inputClass}
                                placeholder="Who is the source — e.g. person, organization, report"
                              />
                            </div>
                            <div>
                              <FieldLabel>Verified Fact</FieldLabel>
                              <textarea
                                value={f.fact}
                                onChange={(e) => patch({ fact: e.target.value })}
                                rows={2}
                                className={inputClass}
                                placeholder="Clean statement without the attribution wrapper"
                              />
                            </div>
                            <div>
                              <FieldLabel>Original Article Text</FieldLabel>
                              <textarea
                                value={f.source_quote || ''}
                                onChange={(e) => patch({ source_quote: e.target.value })}
                                rows={2}
                                className={inputClass}
                                placeholder="Full verbatim passage from the article, including the attribution"
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </section>
              </>
            )}
          </main>
          <aside className="ink-split-aside">
            {cms ? (
              <>
                <CmsPublishCard
                  article={cms}
                  editing={isEditing}
                  disabled={restoring || atomizing}
                  onChanged={refreshAll}
                  onDelete={() => void handleDelete()}
                  deleting={deleting}
                  agentAsk={agentMade ? { name: agentName } : null}
                />
                <ArticleChecks article={cms} editing={isEditing} onChanged={refetchCms} />
              </>
            ) : (
              <div className="ink-rail-card">
                <div className="ink-rail-status" style={{ color: statusColor(item.publishStatus) }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
                    <span
                      className="ink-dot"
                      style={{ background: statusColor(item.publishStatus) }}
                    />
                    {publishStatusLabel(item.publishStatus)}
                  </span>
                  {versions.length > 0 ? (
                    <span className="ink-rail-kicker">
                      v{Math.max(...versions.map((v) => v.versionNumber))}
                    </span>
                  ) : null}
                </div>
                <div className="ink-rail-note">
                  {item.publishStatus === 'published' && item.publishedDate
                    ? `Live since ${format(new Date(item.publishedDate), 'MMM d, yyyy')}.`
                    : item.publishStatus === 'scheduled' && item.publishedDate
                      ? `Goes live ${format(new Date(item.publishedDate), 'MMM d, yyyy, h:mm a')}.`
                      : 'This is a draft. It is not on the site.'}
                </div>
                {item.publishStatus !== 'published' ? (
                  <div className="ink-rail-actions">
                    <button
                      type="button"
                      className="ink-btn ink-btn-primary"
                      onClick={handlePublish}
                      disabled={publishBusy || restoring || atomizing}
                    >
                      {publishing ? 'Publishing…' : 'Publish now'}
                    </button>
                    <button
                      type="button"
                      className="ink-btn"
                      onClick={() => {
                        setScheduleOpen((o) => !o);
                        setMessage('');
                        if (item.publishedDate && item.publishStatus === 'scheduled') {
                          setScheduleAt(
                            snapDatetimeLocalToQuarter(
                              toDatetimeLocalValue(new Date(item.publishedDate))
                            )
                          );
                        } else if (!scheduleAt) {
                          setScheduleAt(toDatetimeLocalValue(nextQuarterHour()));
                        }
                      }}
                      disabled={publishBusy || restoring || atomizing}
                    >
                      Schedule
                    </button>
                  </div>
                ) : null}
                {scheduleOpen && !isEditing && item.publishStatus !== 'published' ? (
                  <div style={{ marginTop: 12 }}>
                    <Input
                      type="datetime-local"
                      step={900}
                      value={scheduleAt}
                      onChange={(e) => setScheduleAt(snapDatetimeLocalToQuarter(e.target.value))}
                      className={inputClass}
                    />
                    <div className="ink-rail-note">Quarter hours only (:00, :15, :30, :45)</div>
                    <div className="ink-rail-actions">
                      <button
                        type="button"
                        className="ink-btn ink-btn-primary"
                        onClick={handleSchedule}
                        disabled={scheduling || !scheduleAt}
                      >
                        {scheduling ? 'Scheduling…' : 'Confirm'}
                      </button>
                      <button
                        type="button"
                        className="ink-btn"
                        onClick={() => setScheduleOpen(false)}
                        disabled={scheduling}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : null}
                {/* One Preview: the header's (Prototype v7). */}
                <div className="ink-rail-links" style={{ justifyContent: 'flex-end' }}>
                  {item.publishStatus !== 'draft' ? (
                    <button
                      type="button"
                      onClick={handleUnpublish}
                      disabled={publishBusy || restoring || atomizing}
                      style={{
                        background: 'none',
                        border: 0,
                        padding: 0,
                        cursor: 'pointer',
                        color: 'var(--ink-ink3)',
                        font: '500 13px var(--ink-font)',
                      }}
                    >
                      {unpublishing ? '…' : 'Unpublish'}
                    </button>
                  ) : null}
                  {
                    <button
                      type="button"
                      onClick={() => void handleDelete()}
                      disabled={publishBusy || restoring || atomizing}
                      style={{
                        background: 'none',
                        border: 0,
                        padding: 0,
                        cursor: 'pointer',
                        color: 'var(--ink-amber)',
                        font: '500 13px var(--ink-font)',
                      }}
                    >
                      {deleting
                        ? 'Deleting…'
                        : item.publishStatus === 'draft'
                          ? 'Delete draft'
                          : 'Delete'}
                    </button>
                  }
                </div>
              </div>
            )}
            <section data-part="classification" className="ink-rail-block">
              <div className="ink-rail-label">Classification</div>
              <dl className="ink-rail-meta">
                <dt>Topic</dt>
                <dd>
                  {isEditing ? (
                    <Input
                      value={fields.category}
                      onChange={(e) => setField('category', e.target.value)}
                      placeholder="Category"
                      className={inputClass}
                    />
                  ) : (
                    topicLabel(item.category) || '—'
                  )}
                </dd>
                {cms ? (
                  <>
                    <dt>Author</dt>
                    <dd>
                      {isEditing ? (
                        <input
                          className="ink-input"
                          style={{ fontSize: 13.5, padding: '6px 9px' }}
                          value={cmsFields.author}
                          onChange={(e) => setCmsField({ author: e.target.value })}
                          placeholder={item.dataSource?.name || 'Author name'}
                        />
                      ) : (
                        cms.content.author || item.dataSource?.name || '—'
                      )}
                    </dd>
                  </>
                ) : null}
                <dt>Tags</dt>
                <dd>
                  {cms ? (
                    isEditing ? (
                      <TagEditor tags={cmsFields.tags} onChange={(tags) => setCmsField({ tags })} />
                    ) : (
                      <span className="ink-rail-terms">
                        {cms.content.tags.map((term) => (
                          <span className="ink-rail-chip" key={term}>
                            {term}
                          </span>
                        ))}
                        {!cms.content.tags.length ? '—' : null}
                      </span>
                    )
                  ) : isEditing ? (
                    <Input
                      value={fields.keywords}
                      onChange={(e) => setField('keywords', e.target.value)}
                      placeholder="Comma-separated keywords"
                      className={inputClass}
                    />
                  ) : (
                    <span className="ink-rail-terms">
                      {(item.keywords || '')
                        .split(',')
                        .map((term) => term.trim())
                        .filter(Boolean)
                        .map((term) => (
                          <span className="ink-rail-chip" key={term}>
                            {term}
                          </span>
                        ))}
                      {!(item.keywords || '').trim() ? '—' : null}
                    </span>
                  )}
                </dd>
                {isEditing && !cms ? (
                  <>
                    <dt>Type</dt>
                    <dd style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {(['webpage', 'video', 'image'] as const).map((type) => (
                        <button
                          key={type}
                          type="button"
                          onClick={() => setField('contentType', type)}
                          className="ink-btn ink-btn-quiet"
                          style={
                            fields.contentType === type
                              ? { background: 'var(--ink-bg3)', color: 'var(--ink-ink)' }
                              : undefined
                          }
                        >
                          {type}
                        </button>
                      ))}
                    </dd>
                  </>
                ) : null}
                {dimensions.map((dim) => {
                  const values = selected[dim.key] || [];
                  if (!isEditing && values.length === 0) return null;
                  return (
                    <span key={dim.key} style={{ display: 'contents' }}>
                      <dt>{dimensionLabel(dim.key)}</dt>
                      <dd>
                        {!isEditing ? (
                          <span className="ink-rail-terms">
                            {values.map((term) => (
                              <span className="ink-rail-chip" key={term}>
                                {term}
                              </span>
                            ))}
                          </span>
                        ) : dim.isFreeform || dim.terms.length === 0 ? (
                          <Input
                            placeholder="Comma-separated values"
                            value={values.join(', ')}
                            onChange={(e) => setFreeform(dim.key, e.target.value)}
                            className={inputClass}
                          />
                        ) : (
                          <span className="ink-rail-terms">
                            {dim.terms.map((term) => {
                              const on = values.includes(term);
                              return (
                                <button
                                  key={term}
                                  type="button"
                                  data-on={on ? 'true' : 'false'}
                                  className="ink-rail-chip"
                                  onClick={() => toggleTerm(dim.key, term)}
                                >
                                  {term}
                                </button>
                              );
                            })}
                          </span>
                        )}
                      </dd>
                    </span>
                  );
                })}
                {Object.entries(syncedByKey)
                  .filter(([key]) => !dimensions.some((dim) => dim.key === key))
                  .map(([key, terms]) => (
                    <span key={key} style={{ display: 'contents' }}>
                      <dt>{dimensionLabel(key)}</dt>
                      <dd>
                        <span className="ink-rail-terms">
                          {terms.map((term) => (
                            <span className="ink-rail-chip" key={term}>
                              {term}
                            </span>
                          ))}
                        </span>
                      </dd>
                    </span>
                  ))}
              </dl>
            </section>
            {atomizeOn && (
              <div className="ink-rail-block">
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'baseline',
                  }}
                >
                  <div className="ink-rail-label" style={{ margin: 0 }}>
                    Atomize
                  </div>
                  <span className="ink-rail-kicker">ADD-ON</span>
                </div>
                <div className="ink-rail-note">
                  {item.atomizedAt
                    ? `Atomized ${format(new Date(item.atomizedAt), 'MMM d, yyyy')}. Facts and entities are on this article.`
                    : 'Not atomized yet. Extract facts and entities from the article.'}
                </div>
                <button
                  type="button"
                  className="ink-btn"
                  style={{ width: '100%', marginTop: 12 }}
                  onClick={() => void handleAtomize()}
                  disabled={atomizing || publishBusy || restoring}
                >
                  {atomizing ? 'Atomizing…' : 'Extract facts & entities'}
                </button>
              </div>
            )}
            <ReachCard reachOn={reachOn} />
            <section data-part="history" className="ink-rail-block">
              <h3 className="ink-rail-label">Approvals & history</h3>
              {cms ? (
                <div style={{ marginBottom: 18 }}>
                  <ArticleApprovals articleId={cms.id} refreshKey={approvalsKey} />
                </div>
              ) : null}
              <div className="ink-rail-kicker" style={{ margin: '0 0 10px' }}>
                SAVED VERSIONS
              </div>
              {compareActive && compareVersions.length >= 2 ? (
                <VersionCompareView
                  versions={compareVersions}
                  onClose={() => {
                    setCompareActive(false);
                    setCompareSelecting(true);
                  }}
                />
              ) : (
                <>
                  <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
                    <p className="text-xs text-cmp-ink-3 max-w-xl">
                      {cms
                        ? 'Snapshots of the working copy from each save. Restore puts one back on the working copy; nothing goes live until you publish.'
                        : 'Full snapshots from create and each save. Restore replaces the current record and adds a new revision.'}
                      {compareSelecting && (
                        <span className="block mt-1 text-cmp-ink">
                          Select 2–3 versions to compare side by side.
                          {compareSelectedIds.length >= 3 ? ' Maximum of 3 selected.' : ''}
                        </span>
                      )}
                    </p>
                    <div className="flex flex-wrap items-center gap-2">
                      {compareSelecting ? (
                        <>
                          <Button
                            variant="outline"
                            className="h-8 text-xs"
                            onClick={cancelCompareSelect}
                          >
                            Cancel
                          </Button>
                          <Button
                            className="h-8 text-xs gap-1.5"
                            disabled={compareSelectedIds.length < 2}
                            onClick={runCompare}
                          >
                            <Columns2 className="size-3.5" />
                            Compare
                            {compareSelectedIds.length > 0 ? ` (${compareSelectedIds.length})` : ''}
                          </Button>
                        </>
                      ) : (
                        <Button
                          variant="outline"
                          className="h-8 text-xs gap-1.5"
                          disabled={versions.length < 2}
                          onClick={startCompareSelect}
                        >
                          <Columns2 className="size-3.5" />
                          Compare
                        </Button>
                      )}
                    </div>
                  </div>
                  {versionsLoading ? (
                    <Loader label="Loading history" className="text-sm text-cmp-ink-2" />
                  ) : versions.length === 0 ? (
                    <p className="text-sm text-cmp-ink-2">
                      No versions yet. Save an edit to start tracking revisions.
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {versions.map((v) => {
                        const snap = v.snapshot || {};
                        const open = expandedVersionId === v.id;
                        const taxKeys = snap.taxonomies ? Object.keys(snap.taxonomies).length : 0;
                        const factCount = snap.atoms?.verified_facts?.length ?? 0;
                        const checked = compareSelectedIds.includes(v.id);
                        const atMax = compareSelectedIds.length >= 3 && !checked;
                        return (
                          <div
                            key={v.id}
                            className={`border rounded p-3 space-y-2 ${
                              checked ? 'border-primary bg-primary/5' : 'border-cmp-line'
                            }`}
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex items-start gap-3 flex-1 min-w-0">
                                {compareSelecting && (
                                  <div className="pt-1 flex-shrink-0">
                                    <Checkbox
                                      checked={checked}
                                      onCheckedChange={() => toggleCompareSelect(v.id)}
                                      className={
                                        atMax
                                          ? 'opacity-40 pointer-events-none'
                                          : 'border-cmp-line-2'
                                      }
                                    />
                                  </div>
                                )}
                                <button
                                  type="button"
                                  className="text-left flex-1 min-w-0"
                                  onClick={() => {
                                    if (compareSelecting) {
                                      if (!atMax || checked) {
                                        toggleCompareSelect(v.id);
                                      }
                                      return;
                                    }
                                    setExpandedVersionId(open ? null : v.id);
                                  }}
                                >
                                  <div className="flex flex-wrap items-center gap-2">
                                    <span className="text-sm text-cmp-ink font-medium">
                                      Version {v.versionNumber}
                                    </span>
                                    <Badge variant="outline" className="text-cmp-ink">
                                      {changeTypeLabel(v.changeType)}
                                    </Badge>
                                  </div>
                                  <p className="text-xs text-cmp-ink-2 mt-1">
                                    {format(new Date(v.createdAt), 'PPpp')}
                                    {v.createdByEmail ? ` · ${v.createdByEmail}` : ''}
                                  </p>
                                  <p className="text-sm text-cmp-ink mt-1 truncate">
                                    {snap.title || 'Untitled'}
                                  </p>
                                </button>
                              </div>
                              {!compareSelecting && (
                                <Button
                                  variant="outline"
                                  className="flex-shrink-0 h-8 text-xs"
                                  disabled={restoring}
                                  onClick={() => handleRestore(v)}
                                >
                                  {restoring ? 'Restoring…' : 'Restore'}
                                </Button>
                              )}
                            </div>
                            {open && !compareSelecting && (
                              <div className="pt-2 border-t border-cmp-line space-y-2 text-xs text-cmp-ink">
                                <p>
                                  <span className="text-cmp-ink-3">Type: </span>
                                  <span className="capitalize">{snap.contentType || '—'}</span>
                                </p>
                                <p>
                                  <span className="text-cmp-ink-3">Taxonomy dims: </span>
                                  {taxKeys}
                                  <span className="text-cmp-ink-3"> · Facts: </span>
                                  {factCount}
                                </p>
                                {snap.summary && (
                                  <div>
                                    <span className="text-cmp-ink-3 block mb-1">Summary</span>
                                    <div
                                      className="line-clamp-4 [&_p]:mb-1"
                                      dangerouslySetInnerHTML={{
                                        __html: toSafeHtml(snap.summary),
                                      }}
                                    />
                                  </div>
                                )}
                                {snap.bodyText && (
                                  <div>
                                    <span className="text-cmp-ink-3 block mb-1">Body</span>
                                    <div
                                      className="line-clamp-6 [&_p]:mb-1"
                                      dangerouslySetInnerHTML={{
                                        __html: toSafeHtml(snap.bodyText),
                                      }}
                                    />
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              )}
            </section>
            <div className="ink-rail-foot">
              ID {item.id}
              {item.dataSource ? ` · Source: ${item.dataSource.name}` : ''}
            </div>
          </aside>
        </>
      )}
    </div>
  );
}
